import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * A moment, as shadcn composes one: a button that opens a calendar, a time field
 * beside it, and -- since every moment we ask for is optional -- a clear button.
 *
 * The value is epoch milliseconds or null, the same shape the API uses, so callers
 * hold no intermediate string. The calendar and the time field edit the two halves
 * of one Date in local time; picking a day when there is no value yet lands on
 * `defaultTime`, the end of that day, because every moment here is a "closes at".
 */
export function DateTimePicker({
  id,
  value,
  onChange,
  defaultTime = "23:59",
  placeholder = "Pick a date",
  className,
}: {
  id?: string;
  value: number | null;
  onChange: (ms: number | null) => void;
  /** `HH:mm` a first-picked day is given. */
  defaultTime?: string;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const date = value === null ? null : new Date(value);

  function pickDay(day: Date | undefined) {
    if (!day) return;
    const [h, m] = date ? [date.getHours(), date.getMinutes()] : parseTime(defaultTime);
    onChange(new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m).getTime());
    setOpen(false);
  }

  function setTime(text: string) {
    // Chrome reports "" while the field is half-typed; leave the value alone until it
    // is a time again rather than wiping the date from under the user.
    if (!date || !text) return;
    const [h, m] = parseTime(text);
    onChange(new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m).getTime());
  }

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          id={id}
          render={
            <Button
              variant="outline"
              data-empty={!date}
              className="min-w-0 flex-1 justify-start font-normal data-[empty=true]:text-muted-foreground"
            />
          }
        >
          <CalendarIcon data-icon="inline-start" />
          <span className="truncate">{date ? format(date, "PP") : placeholder}</span>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={date ?? undefined}
            defaultMonth={date ?? undefined}
            onSelect={pickDay}
          />
        </PopoverContent>
      </Popover>
      <Input
        type="time"
        aria-label="Time"
        disabled={!date}
        value={date ? format(date, "HH:mm") : ""}
        onChange={(e) => setTime(e.target.value)}
        className="w-auto appearance-none [&::-webkit-calendar-picker-indicator]:hidden"
      />
      {date ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Clear"
          onClick={() => onChange(null)}
          className="text-muted-foreground"
        >
          <X />
        </Button>
      ) : null}
    </div>
  );
}

function parseTime(text: string): [number, number] {
  const [h, m] = text.split(":").map(Number);
  return [h || 0, m || 0];
}
